import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { type ErasureRequestDTO, ErasureStatus } from '@today/types'
import type { ErasureRequestListViewProps, ErasureRowView } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { COMPLIANCE_NAMESPACE } from '@/i18n'
import { complianceService } from '@/services/compliance.service'
import { formatDate, listParams, statusOptions } from './compliance-ui'

/** The query-key root every compliance mutation invalidates. */
export const ERASURES_KEY = ['compliance', 'erasures'] as const

/**
 * The deletions list (TO-232 compliance-admin-ui REQ-202): 20 per page, newest
 * first (server order), with a status filter that resets to page 1.
 */
export function useErasureList(onOpen: (id: string) => void): ErasureRequestListViewProps {
	const { t, i18n } = useTranslation(COMPLIANCE_NAMESPACE)
	const [status, setStatus] = useState('')
	const [page, setPage] = useState(1)
	const params = listParams(page, status)
	const list = useQuery({
		queryKey: [...ERASURES_KEY, params],
		queryFn: () => complianceService.listErasures(params),
		placeholderData: keepPreviousData,
	})
	const toRow = (request: ErasureRequestDTO): ErasureRowView => ({
		id: request.id,
		customerId: request.customerId,
		statusLabel: t(`erasureStatus.${request.status}`),
		basisLabel: t(`basis.${request.basis}`),
		requestedAt: formatDate(request.requestedAt, i18n.language),
		graceEndsAt: formatDate(request.graceEndsAt, i18n.language),
	})
	return {
		t,
		rows: (list.data?.data ?? []).map(toRow),
		statusFilter: status,
		statusOptions: statusOptions(Object.values(ErasureStatus), (s) => t(`erasureStatus.${s}`), t('common.all')),
		page,
		pageCount: list.data?.totalPages ?? 0,
		error: list.isError,
		onStatusChange: (next) => {
			setStatus(next)
			setPage(1)
		},
		onPageChange: setPage,
		onOpen,
		onRetry: () => void list.refetch(),
	}
}
